import type { ChartType, Plugin } from 'chart.js'
import { getTheme } from 'themes'

type DrawBandOnHoverOpts = {
  color?: string
  disabled?: boolean
}

export const drawBandOnHover: Plugin<ChartType, DrawBandOnHoverOpts> = {
  id: 'drawBandOnHover',

  // Draw behind datasets (so bars stay on top)
  beforeDatasetsDraw(chart, _args, opts) {
    if (opts?.disabled) return

    const theme = getTheme('light')

    const tooltip: any = chart.tooltip
    const active = tooltip?.getActiveElements?.()?.[0]

    if (!active) return

    const index = active.index
    const indexAxis = chart.options.indexAxis === 'y' ? 'y' : 'x' // bar: 'y' means horizontal bars
    const scale = indexAxis === 'x' ? chart.scales.x : chart.scales.y

    if (!scale) return

    const ctx = chart.ctx
    const { top, bottom, left, right } = chart.chartArea

    // Pixel center of the hovered category
    const center = scale.getPixelForValue(index)

    // Compute the band thickness:
    // - prefer distance to neighbor category centers
    // - fallback to scale.getPixelForTick if present
    let step = 0
    const prev = index > 0 ? scale.getPixelForValue(index - 1) : undefined
    const next =
      index < (chart.data.labels?.length ?? 0) - 1
        ? scale.getPixelForValue(index + 1)
        : undefined

    if (typeof prev === 'number' && typeof next === 'number') {
      step = (next - prev) / 2
    } else if (typeof next === 'number') {
      step = next - center
    } else if (typeof prev === 'number') {
      step = center - prev
    } else {
      // single label case — make a reasonable band
      step =
        (indexAxis === 'x' ? right - left : bottom - top) /
        Math.max(1, chart.data.labels?.length ?? 1)
    }

    const start = center - step / 2
    const end = center + step / 2

    ctx.save()
    ctx.fillStyle = opts?.color ?? theme.palette.action.hover

    if (indexAxis === 'x') {
      // vertical bars -> band is a vertical strip
      ctx.fillRect(start, top, end - start, bottom - top)
    } else {
      // horizontal bars -> band is a horizontal strip
      ctx.fillRect(left, start, right - left, end - start)
    }

    ctx.restore()
  }
}
